/* eslint-disable @typescript-eslint/no-explicit-any */
// TODO: This file was automatically migrated to TypeScript by `dev typescript migrate`.
//  The eslint disable comment above should be removed and the resulting errors fixed.
// TODO(T11394): the eslint and JSX indentation rules compete with each other, resolve this.

import React, {ReactNode} from 'react';
import _ from 'underscore';

import {assert, assertAndContinue} from 'Asserts';
import {
  OFF_TARGET_SCORE_TYPE,
  DESIGN_TYPE,
  SORT_KEY,
  URLS,
  CRISPRGuide,
  CRISPRAnalysisSequenceAttributes,
} from 'constants/CRISPRConstants';
import BaseEditNewSequence from 'molbio/crispr/BaseEditNewSequence';
import * as BaseEditUtil from 'molbio/crispr/BaseEditUtil';
import CRISPRAnalysisStore from 'molbio/crispr/CRISPRAnalysisStore';
import * as CRISPRUtil from 'molbio/crispr/CRISPRUtil';
import GuideGroupTable, {
  GuideGroupTableColumnSpec,
  GuideGroupTableHelpOptions,
} from 'molbio/crispr/GuideGroupTable';
import OffTargetModal from 'molbio/crispr/OffTargetModal';
import * as sequtil from 'molbio/SeqUtil';

export function OffTargetScore({
  type,
  offTargetWarning,
}: {
  type: OFF_TARGET_SCORE_TYPE;
  offTargetWarning?: ReactNode;
}): JSX.Element {
  switch (type) {
    case OFF_TARGET_SCORE_TYPE.MIT: {
      return (
        <div>
          <a href={URLS.HSU_OFF_TARGET_PAPER} target="_blank" rel="noopener noreferrer">
            Specificity score
          </a>{' '}
          from Hsu et al. (2013).
          {offTargetWarning}
          <br />
          <br />
          Score is from 0-100. Higher is better.
        </div>
      );
    }
    case OFF_TARGET_SCORE_TYPE.CFD: {
      return (
        <div>
          Cutting frequency determination (CFD) score from
          <a href={URLS.CFD_SCORE_PAPER} target="_blank" rel="noopener noreferrer">
            Doench, Fusi et al. (2016).
          </a>
          <br />
          <br />
          Score is from 0-100. Higher is better.
        </div>
      );
    }
    default:
      throw new Error(`Unexpected off target score type: ${type}`);
  }
}

function BaseEditScore(): JSX.Element {
  return (
    <div>
      Base edit score is determined by the efficiency of BE1 activity in vitro using an NGG PAM
      <a
        href="http://www.nature.com/nature/journal/v533/n7603/full/nature17946.html"
        target="_blank"
        rel="noopener noreferrer"
      >
        (Komor et al., Nature, 2016, Fig 2b)
      </a>
      .
    </div>
  );
}

interface Props {
  allowEdits: boolean;
  analysis: any;
  guideGroups: Array<Array<CRISPRGuide>>;
  guideGroupSize: number;
  sequence: CRISPRAnalysisSequenceAttributes;
  setSelection: (...args: Array<any>) => any;
  guideGroupSort: {
    key: string;
    reverse: boolean;
    offset: number;
  };
  selectedGuideGroups: any;
  openAssemblyTab: (...args: Array<any>) => any;
  sequencePrimers: Array<any>;
  selection: any;
  saveGuidePrimers: (...args: Array<any>) => any;
  showGuidePrimers: (...args: Array<any>) => any;
}

type State = any;

class BaseEditGuideGroupTable extends React.Component<Props, State> {
  state: State = {
    offTargetModalGuide: null,
  };

  getColumnSpec = (): Array<GuideGroupTableColumnSpec> => {
    const offTargetHelp: GuideGroupTableHelpOptions = {
      title: 'Off-target score',
      content: <OffTargetScore type={OFF_TARGET_SCORE_TYPE.MIT} />,
    };
    const baseEditScoresHelp: GuideGroupTableHelpOptions = {
      title: 'Base-editing scores',
      content: <BaseEditScore />,
    };
    const guideStrandHelp: GuideGroupTableHelpOptions = {
      title: 'sgRNA sequence strand',
      content:
        'Denotes the strand the guide occurs on. Note that the guide is always displayed in terms of its ' +
        'forward strand bases for consistency with the translations displayed in the table.',
    };
    const newSequenceHelp: GuideGroupTableHelpOptions = {
      title: 'Base Edited DNA',
      content: `Red bases designate edited bases, and gray bases designate bases that fall outside of the
          protospacer region.`,
    };

    return [
      {
        sortKey: SORT_KEY.POSITION,
        header: 'Position',
      },
      {
        header: 'Guide strand',
        help: guideStrandHelp,
      },
      {
        header: 'Base edited DNA',
        help: newSequenceHelp,
      },
      {
        sortKey: SORT_KEY.NUM_BASE_EDITS,
        header: '# Edits',
      },
      {
        sortKey: SORT_KEY.BASE_EDIT_SCORES,
        header: 'Base-editing scores',
        help: baseEditScoresHelp,
      },
      {
        sortKey: SORT_KEY.SPECIFICITY_SINGLE,
        header: 'Off\u2011target',
        extraHeader: 'Score',
        help: offTargetHelp,
      },
    ];
  };

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  getEditScores = (guide: any) =>
    BaseEditUtil.getForwardStrandEditScores(guide, this.props.sequence).filter((s: any) => s > 0);

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  getNumEdits = (guide: any) => this.getEditScores(guide).length;

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  getGuideGroupComponentsMap = () => {
    const guideGroupsComponentMap: {[key: string]: Array<Array<JSX.Element>>} = {};
    this.props.guideGroups.forEach((guideGroup) => {
      const guide = guideGroup[0];
      guideGroupsComponentMap[CRISPRAnalysisStore.guideGroupKey(guideGroup)] = [
        [
          <td className="text-left" key="position">
            {sequtil.modelStartToDisplay(this.getGuidePosition(guide), this.props.sequence.startIndex)}
          </td>,
          <td className="mono text-left" key="strand">
            {CRISPRUtil.renderStrand(guide)}
          </td>,
          <td key="bases">
            <BaseEditNewSequence
              guide={guide}
              sequence={this.props.sequence}
              setSelection={this.props.setSelection}
            />
          </td>,
          <td className="text-left" key="numEdits">
            {this.getNumEdits(guide)}
          </td>,
          <td className="text-left" key="baseEditScores">
            {this.renderEditScores(guide)}
          </td>,
          <td className="text-left" key="offTargetScore">
            {this.renderOffTargetScore(guide)}
          </td>,
        ],
      ];
    });
    return guideGroupsComponentMap;
  };

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  getGuidePosition = (guide: any) => CRISPRUtil.guideStart(guide, this.props.sequence.length);

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  getMaxBaseEditScore = (guide: any) => _.max(this.getEditScores(guide));

  getSortIteratee = () => (guideGroup: any) => {
    const guide = guideGroup[0];
    assert(guideGroup.length === 1, 'expected guide group to have length 1');
    const sortKey = this.props.guideGroupSort.key;
    switch (sortKey) {
      case SORT_KEY.POSITION:
        return this.getGuidePosition(guide);
      case SORT_KEY.NUM_BASE_EDITS:
        return this.getNumEdits(guide);
      case SORT_KEY.BASE_EDIT_SCORES:
        return this.getMaxBaseEditScore(guide);
      case SORT_KEY.SPECIFICITY_SINGLE:
        return this.offTargetScore(guide);
      default:
        assertAndContinue(false, `Unrecognized key in guide group sorting function: ${sortKey}`);
        return this.getGuidePosition(guide);
    }
  };

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  getEditedForwardStrandBases = (guide: any) => {
    const forwardStrandBases = CRISPRUtil.getForwardStrandBases(guide);
    const editScores = BaseEditUtil.getForwardStrandEditScores(guide, this.props.sequence);
    return forwardStrandBases
      .split('')
      .map((base, i) => {
        if (editScores[i] > 0) {
          return BaseEditUtil.getForwardStrandEditedBase(base, guide.strand);
        }
        return base;
      })
      .join('');
  };

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  guidesToTSV = (guides: any) => {
    const tsvHeaderRows = [
      ['Position', 'Guide', 'Guide Strand', "Edited 5'->3' Sequence", 'Edit Scores', 'Efficiency Score'].join(
        '\t'
      ),
    ];
    const tsvRows = guides.map((guide: any) =>
      [
        sequtil.modelStartToDisplay(this.getGuidePosition(guide), this.props.sequence.startIndex),
        guide.bases,
        guide.strand,
        this.getEditedForwardStrandBases(guide),
        this.renderEditScores(guide),
        this.offTargetScore(guide),
      ].join('\t')
    );
    return tsvHeaderRows.concat(tsvRows).join('\n');
  };

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  offTargetScore = (guide: any) => guide.offTargetScore;

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  showOffTargetModal = (guide: any) => {
    this.setState({offTargetModalGuide: guide});
  };

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  renderEditScores = (guide: any) =>
    this.getEditScores(guide)
      .map((score: any) => score.toFixed(1))
      .join(', ');

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  renderOffTargetScore = (guide: any) => {
    const score = this.offTargetScore(guide);
    const scoreString = score ? score.toFixed(1) : null;
    return (
      <a
        onClick={() => {
          this.showOffTargetModal(guide);
        }}
      >
        {scoreString}
      </a>
    );
  };

  render(): JSX.Element {
    assert(
      this.props.analysis.designType === DESIGN_TYPE.BASE_EDIT,
      'Cannot render BaseEditGuideGroupTable with non base edit design type'
    );
    // eslint-disable-next-line no-restricted-syntax
    const guideGroupTableProps = _.extend({}, this.props, {
      guideGroupComponentsMap: this.getGuideGroupComponentsMap(),
      columns: this.getColumnSpec(),
      tableClassName: 'crispr-guidesTable',
      getGuidePosition: this.getGuidePosition,
      getSortIteratee: this.getSortIteratee,
      guidesToTSV: this.guidesToTSV,
    });
    const hasInvalidOverlappingTranslations = this.props.guideGroups.some((guideGroup) =>
      BaseEditUtil.hasInvalidOverlappingTranslations(guideGroup[0], this.props.sequence)
    );

    return (
      <div>
        {(() => {
          if (hasInvalidOverlappingTranslations) {
            return (
              <div className="alert alert-warning">
                Guides underlined below overlap with unsupported translations and have been displayed without
                any translations. Unsupported cases include:
                <br />
                (a) Translations on the reverse strand
                <br />
                (b) Overlapping translations
                <br />
                (c) Guides which effect multiple translations
                <br />
                (d) Translations crossing the origin of a circular sequence
              </div>
            );
          }
          return null;
        })()}
        {(() => {
          if (this.state.offTargetModalGuide) {
            return (
              <OffTargetModal
                analysis={this.props.analysis}
                guide={this.state.offTargetModalGuide}
                onRequestClose={() => {
                  this.setState({offTargetModalGuide: null});
                }}
                region={_.findWhere(this.props.analysis.regions, {
                  id: this.state.offTargetModalGuide.regionId,
                })}
              />
            );
          }
          return null;
        })()}
        <GuideGroupTable {...guideGroupTableProps} />
      </div>
    );
  }
}

export default BaseEditGuideGroupTable;
